import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '@/hooks/usePortalAuth';
import { useInspectorProjects } from '@/hooks/useInspectorProjects';
import { useInspections } from '@/hooks/useInspections';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Upload, FolderOpen, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

export default function InspectorDashboard() {
  const navigate = useNavigate();
  const { portalUser, loading: authLoading, updateLastLogin } = usePortalAuth();
  const { data: projects, isLoading: projectsLoading } = useInspectorProjects(portalUser?.id);
  const { inspections, isLoading: inspectionsLoading } = useInspections();

  useEffect(() => {
    if (portalUser) {
      updateLastLogin();
    }
  }, [portalUser, updateLastLogin]);

  if (authLoading || projectsLoading || inspectionsLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <Skeleton className="h-32 w-full" />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      </div>
    );
  }

  // Filter inspections for this inspector
  const myInspections = inspections?.filter(i => i.inspector_id === portalUser?.id) || [];

  const conceptInspections = myInspections.filter(i => i.status === 'concept');
  const ingediendInspections = myInspections.filter(i => i.status === 'ingediend');
  const verwerktInspections = myInspections.filter(i => i.status === 'verwerkt');

  // Calculate stats per project
  const projectStats = projects?.map(project => {
    const projectInspections = myInspections.filter(i => i.project_id === project.id);
    const conceptCount = projectInspections.filter(i => i.status === 'concept').length;
    const ingediendCount = projectInspections.filter(i => i.status === 'ingediend').length;
    const lastInspection = projectInspections.sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    )[0];

    return {
      project,
      totalInspections: projectInspections.length,
      conceptCount,
      ingediendCount,
      lastInspectionDate: lastInspection?.date,
    };
  }) || [];

  const getProjectImage = (project: any) => {
    if (project.project_images && Array.isArray(project.project_images) && project.project_images.length > 0) {
      const img = project.project_images[0];
      return typeof img === 'string' ? img : img?.url;
    }
    return null;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Inspector Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Welkom terug, {portalUser?.email}
          </p>
        </div>
        <Button onClick={() => navigate('/inspector/upload')} size="lg">
          <Upload className="mr-2 h-5 w-5" />
          Nieuwe Inspectie
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Projecten</CardTitle>
            <FolderOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{projects?.length || 0}</div>
            <p className="text-xs text-muted-foreground">Toegewezen projecten</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Concept</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{conceptInspections.length}</div>
            <p className="text-xs text-muted-foreground">Nog niet ingediend</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Ingediend</CardTitle>
            <AlertCircle className="h-4 w-4 text-amber-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{ingediendInspections.length}</div>
            <p className="text-xs text-muted-foreground">In behandeling</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Verwerkt</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{verwerktInspections.length}</div>
            <p className="text-xs text-muted-foreground">Afgerond</p>
          </CardContent>
        </Card>
      </div>

      {/* Recent Inspections */}
      <Card>
        <CardHeader>
          <CardTitle>Recente Inspecties</CardTitle>
          <CardDescription>
            Je laatste {myInspections.slice(0, 5).length || 0} inspecties
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!myInspections || myInspections.length === 0 ? (
            <div className="text-center py-12">
              <Upload className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
              <h3 className="mt-4 text-lg font-medium">Nog geen inspecties</h3>
              <p className="text-muted-foreground mt-2">
                Begin met je eerste inspectie door op "Nieuwe Inspectie" te klikken
              </p>
              <Button onClick={() => navigate('/inspector/upload')} className="mt-4">
                <Upload className="mr-2 h-4 w-4" />
                Eerste Inspectie Maken
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {myInspections.slice(0, 5).map((inspection) => (
                <div
                  key={inspection.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => navigate(`/inspector/inspection/${inspection.id}`)}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <h4 className="font-medium">{inspection.type}</h4>
                      <Badge variant={
                        inspection.status === 'concept' ? 'outline' :
                        inspection.status === 'ingediend' ? 'default' :
                        'secondary'
                      }>
                        {inspection.status === 'concept' ? 'Concept' :
                         inspection.status === 'ingediend' ? 'Ingediend' :
                         'Verwerkt'}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {(inspection as any).project?.name} • {format(new Date(inspection.date), 'PPP', { locale: nl })}
                    </p>
                    {inspection.notes && (
                      <p className="text-sm text-muted-foreground mt-2 line-clamp-1">
                        {inspection.notes}
                      </p>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {Array.isArray(inspection.photo_urls) ? inspection.photo_urls.length : 0} foto's
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Projects Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Toegewezen Projecten</CardTitle>
          <CardDescription>
            Projecten waar je inspecties voor kunt uitvoeren
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!projects || projects.length === 0 ? (
            <div className="text-center py-8">
              <FolderOpen className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground mt-4">
                Er zijn nog geen projecten aan jou toegewezen
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {projectStats.map(({ project, totalInspections, conceptCount, ingediendCount, lastInspectionDate }) => {
                const projectImage = getProjectImage(project);

                return (
                  <Card 
                    key={project.id} 
                    className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => navigate(`/inspector/project/${project.id}`)}
                  >
                    {projectImage && (
                      <div className="h-40 overflow-hidden">
                        <img
                          src={projectImage}
                          alt={project.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    )}
                    <CardHeader>
                      <CardTitle className="text-base">{project.name}</CardTitle>
                      {project.project_number && (
                        <CardDescription>#{project.project_number}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <p className="text-sm text-muted-foreground">
                        {project.organization?.name}
                      </p>

                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Eigen inspecties:</span>
                          <Badge variant="secondary">{totalInspections}</Badge>
                        </div>
                        {lastInspectionDate && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Laatste:</span>
                            <span className="text-xs">{format(new Date(lastInspectionDate), 'PP', { locale: nl })}</span>
                          </div>
                        )}
                        {(conceptCount > 0 || ingediendCount > 0) && (
                          <div className="flex gap-2 text-xs">
                            {conceptCount > 0 && <Badge variant="outline">{conceptCount} concept</Badge>}
                            {ingediendCount > 0 && <Badge variant="outline">{ingediendCount} ingediend</Badge>}
                          </div>
                        )}
                      </div>

                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/inspector/upload?project=${project.id}`);
                        }}
                        className="w-full"
                        size="sm"
                      >
                        Nieuwe inspectie
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
